<script setup>
import { ref, computed, watchEffect, provide, defineAsyncComponent } from "vue"
import { useRoute } from "vue-router"
import AdditionalBar from "./components/AdditionalBar.vue"
import { useGlobalStore } from "@/stores/GlobalStore"
import { useStrapiStore } from "@/stores/StrapiStore"
import { useConfigStore } from "@/stores/ConfigStore"
import { useUserStore } from "@/stores/UserStore"
import Header from "./Header/index.vue"
import AddressDialog from '@/components/Address/AddressDialog.vue'
import AddressAlert from '@/components/Address/AddressAlert.vue'

const ReactivateAccount = defineAsyncComponent(() => import('@/components/ReactivateAccount.vue'))
const AdminBanner = defineAsyncComponent(() => import("./components/AdminBanner.vue"))
const NotificationBar = defineAsyncComponent(() =>
  import("./components/NotificationBar.vue")
);
const Sidebar = defineAsyncComponent(() => import("./Sidebar/index.vue"))
const FeedbackDialog = defineAsyncComponent(() => import("./FeedbackDialog/index.vue"))

const globalStore = useGlobalStore()
const strapiStore = useStrapiStore()
const userStore = useUserStore()
const configStore = useConfigStore()
const route = useRoute()

const sidebar = computed(() => globalStore.sidebar);
const windowWidth = computed(() => globalStore.windowWidth);
const showAdminBanner = computed(() => globalStore.showAdminBanner);
const showSystemNotificationBar = computed(
  () => Object.keys(strapiStore.notificationStatus).length !== 0
);
const systemNotificationBar = computed(() => strapiStore.notificationStatus);
const showingAside = computed(() => {
  const excludedRoutes = [
    "export-batch",
    "create-batch",
    "AddTypes",
    "AddSides",
    "AddUpload",
    "AddCert",
  ];
  return !excludedRoutes.some(
    (routePart) => route.fullPath.includes(routePart) || route.name === routePart
  );
});

const showFeedbackDialog = ref({
  open: false,
});

const closeSidebar = () => {
  if (windowWidth.value < 1280) {
    globalStore.sidebar = false;
    localStorage.setItem("sidebar", JSON.stringify(globalStore.sidebar));
  }
};

const closeTabletSidebar = () => {
  if (windowWidth.value >= 768 && windowWidth.value < 1280) {
    if (globalStore.sidebar) {
      globalStore.sidebar = false;
      localStorage.setItem("sidebar", JSON.stringify(globalStore.sidebar));
    }
  }
}

const handleAddAddress = () => {
  globalStore.showAddressDialog = true
}

provide("showFeedbackDialog", showFeedbackDialog);
provide("showingAside", showingAside);
provide("closeSidebar", closeSidebar);
provide("showSystemNotificationBar", showSystemNotificationBar);
provide("systemNotificationBar", systemNotificationBar);

const showAddressBanner = () => {
  const shouldShow = !userStore.user?.address_1 || !userStore.user?.city || !userStore.user?.state || !userStore.user?.postal_code
  globalStore.showAddressBanner = shouldShow
  return shouldShow
}

watchEffect(() => {
  const isHomepageOrRelated = [
    "Homepage",
    "Batches",
    "Inventory",
    "Connections",
  ].includes(route.name);

  if (!localStorage?.sidebar) {
    globalStore.sidebar = isHomepageOrRelated;
  }
});
</script>

<template>
  <NotificationBar />
  <AdminBanner />
  <section
    class="h-full flex pt-12 transition-all duration-300"
    :class="{
      'xl:!pl-72': sidebar,
      '!pt-[88px]': showAdminBanner || showSystemNotificationBar,
      'md:pl-[60px]': showingAside,
      'md:!pl-72': sidebar,
    }"
  >
    <section class="main-layout" v-click-outside="closeTabletSidebar">
      <Header />
      <Sidebar />
    </section>
    <section class="flex flex-col h-full flex-grow" :class="{'bg-[#F9FAFB]': route.path === '/connections/ebay/connect' || route.path === '/connections/ebay/connected' || route.path === '/connections/whatnot/connect'  || route.path === '/connections/whatnot/connected'}">
      <AdditionalBar
        v-if="showAddressBanner()"
        :text="`Additional information is required. Our payment processor requires a complete address. Please enter your home or business address.`"
        :buttonText="`Add Address`"
        :buttonIcon="`heroicons:arrow-right`"
        @handleButtonClick="handleAddAddress"
      />
      <AddressDialog v-if="globalStore.showAddressDialog" />
      <AddressAlert v-if="globalStore.showAddressAlert" />
      <RouterView />
    </section>
  </section>

  <FeedbackDialog />
   <!-- Global Modal - Shows on all pages based on subscription status -->
  <section
    class="
      absolute top-0 left-0 w-full h-full flex-grow min-h-full flex flex-col z-[99] bg-no-repeat bg-cover
      bg-[url('@/assets/images/batches-frame.png')]
    "
    v-if="!configStore.reactivateAccountModal && !['SettingsPlans', 'Homepage'].includes(route.name)"
  >
    <ReactivateAccount />
  </section>
</template>
